import { Injectable } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { SaleNotificationDetails } from '../models/sale-notification-details';
import { BaseSignalRService } from './base-signalr.service';

@Injectable({
  providedIn: 'root'
})
export class JobNotificationsSignalRService extends BaseSignalRService {
  private readonly saleCreatedSubject = new Subject<string>();
  private readonly jobProcessingSubject = new Subject<string>();
  private readonly jobCreatedSubject = new Subject<SaleNotificationDetails | null>();
  private readonly jobCreationFailedSubject = new Subject<SaleNotificationDetails | null>();
  private readonly saleVerifiedSubject = new Subject<string>();
  private readonly saleApprovedSubject = new Subject<SaleNotificationDetails | null>();
  private readonly saleApprovedFailedSubject = new Subject<SaleNotificationDetails | null>();

  readonly saleCreated$ = this.saleCreatedSubject.asObservable();
  readonly jobProcessing$ = this.jobProcessingSubject.asObservable();
  readonly jobCreated$ = this.jobCreatedSubject.asObservable();
  readonly jobCreationFailed$ = this.jobCreationFailedSubject.asObservable();
  readonly saleVerified$ = this.saleVerifiedSubject.asObservable();
  readonly saleApproved$ = this.saleApprovedSubject.asObservable();
  readonly saleApprovedFailed$ = this.saleApprovedFailedSubject.asObservable();

  protected getHubUrl(): string {
    return `${this.apiUrl}/jobNotificationsHub`;
  }

  protected setupEventHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('SaleCreated', (id: string) => this.saleCreatedSubject.next(id));
    this.hubConnection.on('JobProcessing', (id: string) => this.jobProcessingSubject.next(id));
    this.hubConnection.on('JobCreated', (_id: string, details: SaleNotificationDetails | null) => this.jobCreatedSubject.next(details));
    this.hubConnection.on('JobCreationFailed', (_id: string, details: SaleNotificationDetails | null) => this.jobCreationFailedSubject.next(details));
    this.hubConnection.on('SaleVerified', (id: string) => this.saleVerifiedSubject.next(id));
    this.hubConnection.on('SaleApproved', (_id: string, details: SaleNotificationDetails | null) => this.saleApprovedSubject.next(details));
    this.hubConnection.on('SaleApprovedFailed', (_id: string, details: SaleNotificationDetails | null) => this.saleApprovedFailedSubject.next(details));

    this.setupConnectionStateHandlers();

    this.hubConnection.onreconnected(async () => {
      try {
        await this.joinUserGroup();
      } catch (error) {
        console.error('Failed to rejoin user group after reconnection:', error);
      }
    });
  }

  override async startConnection(): Promise<void> {
    await super.startConnection();
    await this.joinUserGroup();
  }

  private async joinUserGroup(): Promise<void> {
    if (this.hubConnection?.state !== HubConnectionState.Connected) {
      console.warn('joinUserGroup skipped: hub not connected, state:', this.hubConnection?.state ?? 'null');
      return;
    }

    const account = this.msalService.instance.getActiveAccount();
    const oid = (account?.idTokenClaims as Record<string, unknown>)?.['oid'] as string
      ?? account?.localAccountId;

    if (!oid) {
      console.error('joinUserGroup failed: unable to resolve user identity from active account');
      return;
    }

    try {
      await this.hubConnection.invoke('JoinUserGroup', oid);
    } catch (error) {
      console.error('Failed to join user notification group:', error);
    }
  }
}
